import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  APP_NAME, CONTACT_EMAIL, EDITEUR, HEBERGEUR, LEGAL_UPDATED_AT, LEGAL_VERSION,
} from '../constants/site';
import './Legal.css';

/**
 * Politique de confidentialité — reprise fidèle de la notice d'information et de
 * consentement rédigée par le cabinet LexIC
 * (`Avocat/DOCUMENTS/consentement doc/Notice_CycleGala_RGPD_V2.3.docx`).
 *
 * Toute modification de fond doit être répercutée dans ce document et dans
 * `website/app/confidentialite/page.js`, puis la version doit être incrémentée
 * dans `constants/site.js` ET côté back (`consent_version`) — la notice (§12)
 * impose un nouveau recueil de consentement en cas de changement des finalités.
 */
const PolitiqueConfidentialite = () => {
  const navigate = useNavigate();

  return (
    <div className="legal-page">
      <div className="legal-page__header">
        <button className="legal-page__back-btn" onClick={() => navigate(-1)}>
          <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <h1 className="legal-page__title">Politique de confidentialité</h1>
      </div>

      <div className="legal-page__card">
        <p className="legal-page__section-text">
          Notice d'information et de consentement — traitement de données personnelles de santé,
          Règlement (UE) 2016/679 (RGPD).
        </p>
        <p className="legal-page__section-text">
          Ce document vous informe, conformément aux articles 13 et 14 du RGPD, de la manière dont
          CycleGala traite vos données personnelles, y compris vos données de santé, et des droits
          dont vous disposez. Il est mis à votre disposition au moment de votre inscription et
          consultable à tout moment depuis votre espace personnel.
        </p>

        <div className="legal-page__divider" />

        <div className="legal-page__section">
          <h2 className="legal-page__section-title">1. Responsable du traitement et délégué à la protection des données</h2>
          <p className="legal-page__section-text">
            L'application {APP_NAME} est éditée par <strong>{EDITEUR.nom}</strong>, {EDITEUR.forme} au
            capital de {EDITEUR.capital}, immatriculée au Registre du Commerce et des Sociétés
            de {EDITEUR.rcsVille} sous le numéro {EDITEUR.rcsNumero}, dont le siège social est
            situé {EDITEUR.siege}. {APP_NAME} est le nom commercial sous lequel {EDITEUR.nom} exploite
            l'application. {EDITEUR.nom} est le responsable de traitement au sens de l'article 4.7 du RGPD.
          </p>
          <p className="legal-page__section-text">
            Contact du référent protection des données :{' '}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>
        </div>

        <div className="legal-page__divider" />

        <div className="legal-page__section">
          <h2 className="legal-page__section-title">2. Données collectées</h2>
          <p className="legal-page__section-text">Nous collectons quatre catégories de données :</p>
          <ul className="legal-page__section-list">
            <li>
              <strong>Données de compte</strong> (obligatoires) : nom, prénom, adresse e-mail,
              mot de passe hashé, date de naissance.
            </li>
            <li>
              <strong>Données de profil</strong> (facultatives, chiffrées) : type de contraception,
              taille, poids.
            </li>
            <li>
              <strong>Données de santé</strong> (facultatives, chiffrées, catégorie sensible
              Art. 9 RGPD) : flux menstruel, température basale, test d'ovulation, pertes vaginales,
              libido, symptômes physiques, état de santé mentale. Ces données ne sont traitées que
              sur la base de votre consentement explicite.
            </li>
            <li>
              <strong>Données techniques</strong> (générées automatiquement) : adresse IP, type
              d'appareil, version de l'application, logs de connexion.
            </li>
          </ul>
        </div>

        <div className="legal-page__divider" />

        <div className="legal-page__section">
          <h2 className="legal-page__section-title">3. Finalités et bases légales</h2>
          <p className="legal-page__section-text">Vos données sont traitées pour les finalités suivantes :</p>
          <ul className="legal-page__section-list">
            <li>Création et gestion de votre compte — exécution du contrat (Art. 6.1.b RGPD).</li>
            <li>
              Fonctionnement de l'application, suivi du cycle et recommandations sportives —
              consentement explicite (Art. 6.1.a et 9.2.a RGPD).
            </li>
            <li>
              Partage de vos données avec votre coach, si vous le choisissez — consentement
              explicite granulaire (Art. 6.1.a et 9.2.a RGPD).
            </li>
            <li>Sécurité du service et prévention des abus — intérêt légitime (Art. 6.1.f RGPD).</li>
            <li>
              Respect de nos obligations comptables et fiscales — obligation légale (Art. 6.1.c RGPD).
            </li>
          </ul>
        </div>

        <div className="legal-page__divider" />

        <div className="legal-page__section">
          <h2 className="legal-page__section-title">4. Profilage et recommandations personnalisées</h2>
          <p className="legal-page__section-text">
            CycleGala utilise vos données de santé pour générer des recommandations sportives
            adaptées à votre situation physiologique. Ce traitement constitue du profilage au sens
            de l'article 4.4 du RGPD : il consiste à analyser automatiquement vos saisies pour
            évaluer et anticiper certains aspects de votre état physique et hormonal.
          </p>
          <p className="legal-page__section-text">
            <strong>Logique et paramètres utilisés.</strong> L'algorithme prend en compte les
            données que vous saisissez — notamment votre type de contraception (pilule, stérilet,
            implant), votre température basale, vos résultats de test d'ovulation, votre état de
            santé mentale et vos symptômes physiques — pour produire une recommandation
            d'entraînement adaptée à votre situation physiologique du moment.
          </p>
          <p className="legal-page__section-text">
            <strong>Portée et limites.</strong> Ces recommandations sont indicatives. Elles ne
            constituent ni un diagnostic médical, ni une prescription. Elles n'ont aucun effet
            juridique sur vous et n'engagent pas la responsabilité médicale de CycleGala.
          </p>
          <p className="legal-page__section-text">
            <strong>Votre maîtrise sur ce traitement.</strong> Ce profilage ne démarre que si vous
            avez donné votre consentement explicite au traitement de vos données de santé. Vous
            pouvez retirer ce consentement à tout moment dans vos paramètres. Le retrait n'entraîne
            pas la suppression de votre compte ni l'impossibilité d'utiliser les autres
            fonctionnalités de l'application.
          </p>
        </div>

        <div className="legal-page__divider" />

        <div className="legal-page__section">
          <h2 className="legal-page__section-title">5. Partage de vos données avec votre coach</h2>
          <p className="legal-page__section-text">
            Le partage est strictement optionnel et soumis à votre consentement explicite. Vous
            décidez, indépendamment pour chaque domaine, ce que votre coach peut voir :
          </p>
          <ul className="legal-page__section-list">
            <li>Données de cycle</li>
            <li>Santé mentale</li>
            <li>Performance sportive</li>
          </ul>
          <p className="legal-page__section-text">
            Vous pouvez modifier ou révoquer ce partage à tout moment depuis vos paramètres. La
            révocation est immédiate. Votre coach est tenu contractuellement à la confidentialité et
            ne peut utiliser vos données qu'aux fins de votre suivi sportif.
          </p>
        </div>

        <div className="legal-page__divider" />

        <div className="legal-page__section">
          <h2 className="legal-page__section-title">6. Destinataires des données</h2>
          <p className="legal-page__section-text">
            Vos données ne sont jamais vendues. Elles peuvent être communiquées aux seules
            catégories de destinataires suivantes :
          </p>
          <ul className="legal-page__section-list">
            <li>
              Le personnel habilité de CycleGala, dans les limites strictement nécessaires au
              fonctionnement du service, soumis à confidentialité.
            </li>
            <li>
              Votre coach, exclusivement sur les données pour lesquelles vous avez donné votre
              consentement (section 5).
            </li>
            <li>
              Notre hébergeur <strong>{HEBERGEUR.nom}</strong> (SIRET {HEBERGEUR.siret},{' '}
              {HEBERGEUR.adresse}), certifié ISO 27001 et HDS (Hébergeur de Données de
              Santé), encadré par un contrat de traitement de données conforme à l'article 28 RGPD.
            </li>
            <li>Les autorités compétentes, uniquement sur réquisition judiciaire ou légale.</li>
          </ul>
        </div>

        <div className="legal-page__divider" />

        <div className="legal-page__section">
          <h2 className="legal-page__section-title">7. Transferts hors de l'Union européenne</h2>
          <p className="legal-page__section-text">
            Vos données sont hébergées exclusivement en France (Union européenne), dans deux
            datacenters situés en métropole brestoise. Aucun transfert hors de l'Union européenne
            n'est réalisé.
          </p>
        </div>

        <div className="legal-page__divider" />

        <div className="legal-page__section">
          <h2 className="legal-page__section-title">8. Durées de conservation</h2>
          <p className="legal-page__section-text">Vos données sont conservées pour les durées suivantes :</p>
          <ul className="legal-page__section-list">
            <li>
              <strong>Données de compte</strong> : durée d'activité du compte, puis 3 ans
              (prescription de droit commun), puis suppression définitive.
            </li>
            <li>
              <strong>Données de profil et de santé</strong> : 6 mois à compter de la dernière
              saisie active, ou jusqu'au retrait de votre consentement, puis suppression définitive.
            </li>
            <li>
              <strong>Logs techniques</strong> : 13 mois, suppression automatique (recommandation CNIL).
            </li>
            <li><strong>Pièces comptables</strong> : 10 ans (obligation légale).</li>
          </ul>
        </div>

        <div className="legal-page__divider" />

        <div className="legal-page__section">
          <h2 className="legal-page__section-title">9. Vos droits</h2>
          <p className="legal-page__section-text">
            Conformément au RGPD et à la loi Informatique et Libertés modifiée (loi n° 2018-493 du
            20 juin 2018), vous disposez des droits suivants :
          </p>
          <ul className="legal-page__section-list">
            <li>
              <strong>Droit d'accès</strong> (Art. 15) : obtenir la liste et une copie des données
              que nous détenons sur vous.
            </li>
            <li>
              <strong>Droit de rectification</strong> (Art. 16) : faire corriger des informations
              inexactes ou incomplètes.
            </li>
            <li>
              <strong>Droit à l'effacement</strong> (Art. 17) : demander la suppression de vos
              données, dans les cas prévus par la loi.
            </li>
            <li>
              <strong>Droit à la limitation</strong> (Art. 18) : demander le gel temporaire du
              traitement de vos données.
            </li>
            <li>
              <strong>Droit à la portabilité</strong> (Art. 20) : recevoir vos données dans un
              format structuré et lisible par machine, ou les faire transmettre à un autre service.
            </li>
            <li>
              <strong>Droit d'opposition</strong> (Art. 21) : vous opposer aux traitements fondés
              sur l'intérêt légitime, notamment les traitements liés à la sécurité du service.
            </li>
            <li>
              <strong>Droit d'opposition au profilage</strong> : CycleGala réalisant un profilage à
              partir de vos données de santé, vous pouvez vous y opposer à tout moment en retirant
              votre consentement depuis vos paramètres. Ce retrait arrête immédiatement la
              production de recommandations personnalisées mais n'entraîne ni la suppression de
              votre compte ni l'impossibilité d'utiliser les autres fonctionnalités de l'application.
            </li>
            <li>
              <strong>Droit au retrait du consentement</strong> : retirer à tout moment votre
              consentement au traitement de vos données de santé et au profilage, sans que cela
              affecte la validité des traitements antérieurs. Le retrait n'entraîne pas la
              suppression de votre compte.
            </li>
            <li>
              <strong>Directives post-mortem</strong> (Art. 85 LIL) : définir des instructions sur
              le sort de vos données après votre décès.
            </li>
          </ul>
        </div>

        <div className="legal-page__divider" />

        <div className="legal-page__section">
          <h2 className="legal-page__section-title">10. Exercice de vos droits et voies de recours</h2>
          <p className="legal-page__section-text">
            Pour exercer l'un des droits listés ci-dessus, contactez notre référent protection des
            données :
          </p>
          <ul className="legal-page__section-list">
            <li>Par e-mail : <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></li>
            <li>
              Par courrier : {EDITEUR.nom} — {APP_NAME}, à l'attention du référent protection des
              données — {EDITEUR.siege}
            </li>
          </ul>
          <p className="legal-page__section-text">
            Votre demande sera traitée dans un délai d'un mois (Art. 12 RGPD), prorogeable de deux
            mois en cas de complexité particulière, avec information préalable. Nous pourrons vous
            demander une pièce d'identité pour vérifier votre identité.
          </p>
          <p className="legal-page__section-text">
            Si vous estimez, après nous avoir contactés, que vos droits ne sont pas respectés, vous
            pouvez introduire une réclamation auprès de la Commission nationale de l'informatique et
            des libertés (CNIL) — 3 place de Fontenoy, TSA 80715, 75334 Paris Cedex 07 —{' '}
            <a href="https://www.cnil.fr" target="_blank" rel="noreferrer">www.cnil.fr</a> — 01 53 73 22 22.
          </p>
        </div>

        <div className="legal-page__divider" />

        <div className="legal-page__section">
          <h2 className="legal-page__section-title">11. Sécurité des données</h2>
          <p className="legal-page__section-text">
            Nous mettons en œuvre des mesures techniques et organisationnelles adaptées, notamment :
            chiffrement de l'ensemble des données de santé et de profil en base de données, accès
            limité aux personnes habilitées avec journalisation des accès, pseudonymisation des
            données utilisées à des fins d'amélioration du service.
          </p>
        </div>

        <div className="legal-page__divider" />

        <div className="legal-page__section">
          <h2 className="legal-page__section-title">12. Mises à jour de la présente notice</h2>
          <p className="legal-page__section-text">
            Nous nous réservons le droit de modifier la présente notice, notamment en cas
            d'évolution réglementaire. Toute modification substantielle vous sera notifiée au moins
            30 jours avant son entrée en vigueur. En cas de modification des finalités de traitement
            de vos données de santé ou du profilage, un nouveau recueil de consentement explicite
            vous sera présenté.
          </p>
        </div>
      </div>

      <p className="legal-page__version">
        Version {LEGAL_VERSION} — {LEGAL_UPDATED_AT}
        <br />
        Rédigée par le Cabinet LexIC — Avocat au Barreau de Brest — Droit du numérique
      </p>
    </div>
  );
};

export default PolitiqueConfidentialite;
